import Icon from '@ant-design/icons'
import { CheckboxOptionType } from 'antd/es/checkbox/Group'
import clsx from 'clsx'
import { FieldArray, FormikContext } from 'formik'
import { BaseSyntheticEvent, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { v4 } from 'uuid'

import styles from './list-config.module.scss'
import { getIcon, IconEnum } from '../../../../../assets/icons'
import { useFormik } from '../../../../../common/hooks/formik'
import { PureButton } from '../../../../../common/ui/pure-button/pure-button'
import { PureInput } from '../../../../../common/ui/pure-input/pure-input'
import { PureSwitcher } from '../../../../../common/ui/pure-switcher/pure-switcher'
import { useFBField } from '../../form-builder.context'
import { FormBuilderConfig } from '../../types/form-builder.types'

type ListConfigProps = {
  label?: string
  required?: boolean
  options?: CheckboxOptionType[]
}

export const CheckboxConfig = ({ label, options, required = false }: ListConfigProps) => {
  const { t } = useTranslation()
  const { value: configValue, setValue } = useFBField()
  const formik = useFormik<ListConfigProps>({
    initialValues: {
      label: label || '',
      options: options || [],
    },
    enableReinitialize: true,
    validationSchema: {},
    onSubmit: (values) => {
      console.log('values', values)
    },
  })

  useEffect(() => {
    const values = formik.values
    setValue({ ...values } as FormBuilderConfig)
  }, [formik.values])

  const handleChange = (key: string, value: BaseSyntheticEvent) => {
    setValue({ ...configValue, [key]: value.target.value } as unknown as FormBuilderConfig)
  }

  const handleSwitchChange = (key: string, value: boolean) => {
    setValue({ ...configValue, [key]: value } as unknown as FormBuilderConfig)
  }

  const getEmptyOption = () => ({
    label: '',
    value: v4(),
  })

  return (
    <FormikContext.Provider value={formik as any}>
      <form onSubmit={formik.handleSubmit}>
        <PureInput
          {...formik.getFieldProps('label')}
          name="label"
          onInput={(e) => handleChange('label', e)}
          placeholder={t('common.name')}
          size="small"
        />
        <hr />
        <FieldArray name="options">
          {({ push, move, remove }) => (
            <>
              {formik.values.options?.map((option, index) => (
                <div
                  key={index}
                  className={clsx('mt-2', 'd-flex', 'justify-content-between', 'align-items-center')}
                >
                  <div className={clsx(styles.positionControls)}>
                    {index > 0 && (
                      <Icon
                        component={getIcon(IconEnum.ArrowUp)}
                        onClick={() => move(index, index - 1)}
                      />
                    )}

                    {index !== (formik.values.options ?? []).length - 1 && (
                      <Icon
                        component={getIcon(IconEnum.ArrowDown)}
                        onClick={() => move(index, index + 1)}
                      />
                    )}
                  </div>

                  <div>
                    <PureInput
                      {...formik.getFieldProps(`options.${index}.label` as 'options')}
                      key={index}
                      placeholder={t('common.option') + ' ' + String(index + 1)}
                      size="small"
                    />
                  </div>

                  <div className={clsx(styles.optionTrash)}>
                    <Icon
                      component={getIcon(IconEnum.Trash)}
                      onClick={() => remove(index)}
                    />
                  </div>
                </div>
              ))}

              <PureButton
                className={clsx(styles.addOptionBtn, 'mt-2')}
                name="placeholder"
                onClick={() => push(getEmptyOption())}
                size="small"
                variant="outlined"
              >
                +
              </PureButton>
            </>
          )}
        </FieldArray>
        <hr />
        <PureSwitcher
          isChecked={required}
          label={[t('formBuilder.requiredForFill'), t('formBuilder.requiredForFill')]}
          onChange={(value) => handleSwitchChange('required', value)}
          position="left"
        />
      </form>
    </FormikContext.Provider>
  )
}
